import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Address from "_shared/addresses/address";
import AddressSchema from "_shared/addresses/address-schema";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import PhoneNumber from "_shared/phone-numbers/phone-number";
import InsuranceInfo from "marketplace/insurance-info/insurance-info";
import RegistrationNumber from "marketplace/registration-number/registration-number";
import BillingRateInfo from "marketplace/teams/fee-schedules/rates/billing-rate-info";
import { BillingRateInfoJSON } from "marketplace/teams/fee-schedules/rates/billing-rate-info-schema";
import TechnicalBackgroundInfo from "marketplace/technical-background-info/technical-background-info";
import { z } from "zod";

export type IndividualProfileJSON = z.infer<typeof IndividualProfileSchema.IndividualProfileJSONZ>;
export type IndividualProfileParams = z.infer<typeof IndividualProfileSchema.IndividualProfileZ>;

export default class IndividualProfileSchema {
  public static readonly IndividualProfileJSONZ = z.object({
    userId: z.uuid().nullish(),
    legalEntityId: z.uuid().nullish(),
    entityName: z.string().nullish(),
    firstName: z.string(),
    lastName: z.string(),
    description: z.string().nullish(),
    location: AddressSchema.AddressJSONZ.nullish(),
    phoneNumber: z.string().nullish(),
    avatarId: z.uuid().nullish(),
    email: z.email().or(z.literal("")).nullish(),
    school: z.string().nullish(),
    degree: z.string().nullish(),
    resume: z.uuid().nullish(),
    professionalRegistration: z.string().nullish(),
    technicalBackground: z.string().nullish(),
    numberOfApplicationsDraftedProsecuted: z.number().nullish(),
    numberOfAllowedApplications: z.number().nullish(),
    barAdmissionYear: z.number().nullish(),
    isOnCall: z.boolean().nullish(),
    insuredAmount: z.number().nullish(),
    billingRate: z.string().nullish(),
    billingUnit: z.string().nullish(),
    potentialDiversityCandidate: z.boolean().nullish(),
    collaborates: z.boolean().nullish(),
    additionalInfo: z.string().nullish(),
    categories: z.array(z.string()).nullish(),
    links: z.array(z.string()).nullish(),
    professionalPublications: z.array(z.string()).nullish(),
    sampleApplications: z.array(z.string()).nullish(),
    fluentLanguages: z.array(z.string()).nullish(),
    technicalLanguages: z.array(z.string()).nullish(),
    administrativeRegions: z.array(z.string()).nullish(),
    regionsRegistered: z.array(z.string()).nullish(),
    regionsLicensed: z.array(z.string()).nullish(),
  });

  public static readonly IndividualProfileZ = z.object({
    userId: z.instanceof(Guid).optional(),
    legalEntityId: z.instanceof(Guid).optional(),
    entityName: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
    description: z.string().optional(),
    location: z.instanceof(Address).optional(),
    phoneNumber: z.custom<PhoneNumber>().optional(),
    avatarId: z.instanceof(Guid).optional(),
    email: z.instanceof(EmailAddress).optional(),
    school: z.string().optional(),
    degree: z.string().optional(),
    resumeId: z.instanceof(Guid).optional(),
    professionalRegistration: z.custom<RegistrationNumber>().optional(),
    technicalBackground: z.custom<TechnicalBackgroundInfo>().optional(),
    numberOfApplicationsDraftedProsecuted: z.number().optional(),
    numberOfAllowedApplications: z.number().optional(),
    barAdmissionYear: z.number().optional(),
    isOnCall: z.boolean().optional(),
    insured: z.instanceof(InsuranceInfo).optional(),
    billingRate: z.instanceof(BillingRateInfo).optional(),
    potentialDiversityCandidate: z.boolean().optional(),
    collaborates: z.boolean().optional(),
    additionalInfo: z.string().optional(),
    categories: z.array(z.string()).optional(),
    links: z.array(z.string()).optional(),
    professionalPublications: z.array(z.string()).optional(),
    sampleApplications: z.array(z.string()).optional(),
    fluentLanguages: z.array(z.string()).optional(),
    technicalLanguages: z.array(z.string()).optional(),
    administrativeRegions: z.array(z.string()).optional(),
    regionsRegistered: z.array(z.string()).optional(),
    regionsLicensed: z.array(z.string()).optional(),
  });

  public static validate(values: unknown): IndividualProfileParams {
    return parseOrThrow(IndividualProfileSchema.IndividualProfileZ, values, {
      logError: true,
      schemaName: "IndividualProfileSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): IndividualProfileJSON {
    return parseOrThrow(IndividualProfileSchema.IndividualProfileJSONZ, json, {
      logError: true,
      schemaName: "IndividualProfileSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is IndividualProfileParams {
    try {
      IndividualProfileSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is IndividualProfileJSON {
    try {
      IndividualProfileSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: IndividualProfileJSON): IndividualProfileParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(IndividualProfileSchema.IndividualProfileJSONZ, json, {
      logError: true,
      schemaName: "IndividualProfileSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return IndividualProfileSchema.codec.decode(parsed);
  }

  public static serialize(params: IndividualProfileParams): IndividualProfileJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = IndividualProfileSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return IndividualProfileSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    IndividualProfileSchema.IndividualProfileJSONZ,
    IndividualProfileSchema.IndividualProfileZ,
    {
      decode: (data) => ({
        userId: Guid.fromJSON(data.userId) ?? undefined,
        legalEntityId: Guid.fromJSON(data.legalEntityId) ?? undefined,
        entityName: data.entityName ?? undefined,
        firstName: data.firstName,
        lastName: data.lastName,
        description: data.description ?? undefined,
        location: Address.fromJSON(data.location) ?? undefined,
        phoneNumber: data.phoneNumber ? new PhoneNumber(data.phoneNumber) : undefined,
        avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
        email: data.email?.length ? (EmailAddress.fromJSON(data.email) ?? undefined) : undefined,
        school: data.school ?? undefined,
        degree: data.degree ?? undefined,
        resumeId: Guid.fromJSON(data.resume) ?? undefined,
        professionalRegistration: data.professionalRegistration
          ? new RegistrationNumber(data.professionalRegistration)
          : undefined,
        technicalBackground: data.technicalBackground
          ? new TechnicalBackgroundInfo(data.technicalBackground)
          : undefined,
        numberOfApplicationsDraftedProsecuted: data.numberOfApplicationsDraftedProsecuted ?? undefined,
        numberOfAllowedApplications: data.numberOfAllowedApplications ?? undefined,
        barAdmissionYear: data.barAdmissionYear ?? undefined,
        isOnCall: data.isOnCall ?? undefined,
        insured: InsuranceInfo.fromJSON({ insuredAmount: data.insuredAmount }) ?? undefined,
        billingRate: BillingRateInfo.fromJSON({
          billingRate: data.billingRate,
          billingUnit: data.billingUnit,
        } as BillingRateInfoJSON) ?? undefined,
        potentialDiversityCandidate: data.potentialDiversityCandidate ?? false,
        collaborates: data.collaborates ?? false,
        additionalInfo: data.additionalInfo ?? undefined,
        categories: data.categories ?? [],
        links: data.links ?? [],
        professionalPublications: data.professionalPublications ?? [],
        sampleApplications: data.sampleApplications ?? [],
        fluentLanguages: data.fluentLanguages ?? [],
        technicalLanguages: data.technicalLanguages ?? [],
        administrativeRegions: data.administrativeRegions ?? [],
        regionsRegistered: data.regionsRegistered ?? [],
        regionsLicensed: data.regionsLicensed ?? [],
      }),
      encode: (profile) => ({
        userId: profile.userId?.toJSON(),
        legalEntityId: profile.legalEntityId?.toJSON(),
        entityName: profile.entityName,
        firstName: profile.firstName ?? "N/A",
        lastName: profile.lastName ?? "N/A",
        description: profile.description,
        location: profile.location?.toJSON(),
        phoneNumber: profile.phoneNumber ? profile.phoneNumber.value : undefined,
        avatarId: profile.avatarId?.toJSON(),
        email: profile.email?.toJSON(),
        school: profile.school,
        degree: profile.degree,
        resume: profile.resumeId?.toJSON(),
        professionalRegistration: profile.professionalRegistration?.value,
        technicalBackground: profile.technicalBackground?.value,
        numberOfApplicationsDraftedProsecuted: profile.numberOfApplicationsDraftedProsecuted,
        numberOfAllowedApplications: profile.numberOfAllowedApplications,
        barAdmissionYear: profile.barAdmissionYear,
        isOnCall: profile.isOnCall,
        insuredAmount: profile.insured?.insuredAmount,
        billingRate: profile.billingRate ? profile.billingRate.billingRate : undefined,
        billingUnit: profile.billingRate ? profile.billingRate.billingUnit : undefined,
        potentialDiversityCandidate: profile.potentialDiversityCandidate,
        collaborates: profile.collaborates,
        additionalInfo: profile.additionalInfo,
        categories: profile.categories ?? [],
        links: profile.links ?? [],
        professionalPublications: profile.professionalPublications ?? [],
        sampleApplications: profile.sampleApplications ?? [],
        fluentLanguages: profile.fluentLanguages ?? [],
        technicalLanguages: profile.technicalLanguages ?? [],
        administrativeRegions: profile.administrativeRegions ?? [],
        regionsRegistered: profile.regionsRegistered ?? [],
        regionsLicensed: profile.regionsLicensed ?? [],
      }),
    },
  );
}
